/**
 * InvoicesTable — Viyora's premium 7-column invoice list.
 * Columns: Invoice · Patient · Treatment · Doctor · Amount · Status · Actions.
 * Includes search, filters, sort, sticky header, and a "More" action menu.
 * All heavy-lift data (payment history, timeline, audit) lives in the
 * <InvoiceDetailsDrawer/> and is only fetched on demand.
 */
import { useMemo, useState } from "react";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Search, ChevronDown, ChevronUp, MoreHorizontal, Eye, Banknote, Printer,
  Download, Mail, MessageCircle, Copy, XCircle, Trash2, History, ClipboardList,
  Filter, FileText,
} from "lucide-react";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", {
  day: "numeric", month: "short", year: "numeric",
}) : "—";

const STATUS_META = {
  paid:      { label: "Paid",      cls: "bg-[#E8F0EB] text-[#2D6A4F] border-[#B8D6C2]" },
  partial:   { label: "Partial",   cls: "bg-[#FFF4E5] text-[#8A4B00] border-[#F2C494]" },
  pending:   { label: "Pending",   cls: "bg-[#FDF3ED] text-[#D06A42] border-[#F5C9B0]" },
  overdue:   { label: "Overdue",   cls: "bg-[#FCECEC] text-[#B94A48] border-[#F0B0AE]" },
  cancelled: { label: "Cancelled", cls: "bg-[#EFEEEA] text-[#6E6D69] border-[#DDDBD3]" },
  refunded:  { label: "Refunded",  cls: "bg-[#EEF0FA] text-[#3C4A8A] border-[#C7CFEB]" },
};

const METHOD_LABEL = {
  cash: "Cash", upi: "UPI", credit_card: "Card", debit_card: "Card",
  bank_transfer: "Bank", neft: "NEFT", rtgs: "RTGS", imps: "IMPS",
  cheque: "Cheque", wallet: "Wallet", razorpay: "Razorpay", stripe: "Stripe",
};

const SORT_KEYS = {
  date_desc:   { label: "Newest first",           key: "created_at",       dir: -1 },
  date_asc:    { label: "Oldest first",           key: "created_at",       dir:  1 },
  amount_desc: { label: "Amount: high → low",     key: "total",            dir: -1 },
  amount_asc:  { label: "Amount: low → high",     key: "total",            dir:  1 },
  due_desc:    { label: "Outstanding: high → low",key: "outstanding_amount", dir: -1 },
  due_asc:     { label: "Outstanding: low → high",key: "outstanding_amount", dir:  1 },
  patient_asc: { label: "Patient A → Z",          key: "patient_name",     dir:  1, str: true },
  doctor_asc:  { label: "Doctor A → Z",           key: "_doctor_key",      dir:  1, str: true },
};

const STATUS_FILTERS = [
  { id: "all",       label: "All" },
  { id: "pending",   label: "Pending" },
  { id: "partial",   label: "Partial" },
  { id: "overdue",   label: "Overdue" },
  { id: "paid",      label: "Paid" },
  { id: "cancelled", label: "Cancelled" },
];


export default function InvoicesTable({
  invoices, canDelete, onOpen, onPreview, onCollect, onEmail, onWhatsapp, onDownloadPdf,
  onPrint, onDuplicate, onCancel, onDelete, onViewPatient,
}) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [method, setMethod] = useState("all");
  const [sortId, setSortId] = useState("date_desc");

  // Distinct methods from actual data — feeds the filter dropdown
  const methodOptions = useMemo(() => {
    const s = new Set();
    for (const i of invoices) for (const m of (i.payment_methods || [])) s.add(m);
    return ["all", ...Array.from(s)];
  }, [invoices]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const rows = invoices
      .map((i) => ({ ...i, _doctor_key: (i.doctor_names?.[0] || "").toLowerCase() }))
      .filter((i) => {
        if (status !== "all" && i.status !== status) return false;
        if (method !== "all" && !(i.payment_methods || []).includes(method)) return false;
        if (!query) return true;
        const hay = [
          i.invoice_number, i.patient_name, i.patient_phone,
          (i.doctor_names || []).join(" "),
          i.treatment_summary?.primary,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(query);
      });
    const s = SORT_KEYS[sortId];
    rows.sort((a, b) => {
      const av = a[s.key]; const bv = b[s.key];
      if (s.str) return s.dir * String(av || "").localeCompare(String(bv || ""));
      return s.dir * ((Number(av) || 0) - (Number(bv) || 0));
    });
    return rows;
  }, [invoices, q, status, method, sortId]);

  return (
    <div className="rounded-3xl bg-white border border-[#EFEADC] overflow-hidden" data-testid="invoices-table-card">
      {/* -------------- toolbar -------------- */}
      <div className="px-5 pt-5 pb-3 border-b border-[#F0EBE0]">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="overline text-ink-soft">All invoices</div>
            <div className="font-display text-2xl text-ink mt-1">{filtered.length} invoice{filtered.length === 1 ? "" : "s"}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
              <input
                data-testid="inv-search"
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search invoice, patient, phone, doctor…"
                className="pl-9 pr-3 py-2 rounded-full border border-[#E8EAE9] text-xs bg-white w-72"
              />
            </div>
            <SortDropdown sortId={sortId} setSortId={setSortId} />
            <FilterPopover
              status={status} setStatus={setStatus}
              method={method} setMethod={setMethod}
              methodOptions={methodOptions}
            />
          </div>
        </div>

        {/* Status pill row */}
        <div className="mt-4 flex items-center gap-1.5 flex-wrap" data-testid="inv-status-tabs">
          {STATUS_FILTERS.map((s) => {
            const count = s.id === "all"
              ? invoices.length
              : invoices.filter((i) => i.status === s.id).length;
            const active = status === s.id;
            return (
              <button
                key={s.id}
                data-testid={`inv-status-tab-${s.id}`}
                onClick={() => setStatus(s.id)}
                className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${
                  active ? "bg-pine text-linen" : "bg-white border border-[#E8EAE9] text-ink-mid hover:bg-linen/70"
                }`}
              >
                {s.label}
                <span className={`text-[10px] ${active ? "text-linen/70" : "text-ink-soft"}`}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* -------------- table -------------- */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="invoices-table">
          <thead className="sticky top-0 bg-white z-[1]">
            <tr className="text-left text-ink-soft overline">
              <th className="px-5 py-3 font-normal w-[15%]">Invoice</th>
              <th className="px-3 py-3 font-normal w-[17%]">Patient</th>
              <th className="px-3 py-3 font-normal w-[17%]">Treatment</th>
              <th className="px-3 py-3 font-normal w-[15%]">Doctor</th>
              <th className="px-3 py-3 font-normal text-right w-[16%]">Amount</th>
              <th className="px-3 py-3 font-normal w-[10%]">Status</th>
              <th className="px-3 py-3 font-normal text-right w-[10%]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1EEE4]">
            {filtered.map((inv) => (
              <InvoiceRow
                key={inv.id} inv={inv} canDelete={canDelete}
                onOpen={onOpen} onPreview={onPreview} onCollect={onCollect} onEmail={onEmail}
                onWhatsapp={onWhatsapp} onDownloadPdf={onDownloadPdf}
                onPrint={onPrint} onDuplicate={onDuplicate}
                onCancel={onCancel} onDelete={onDelete}
                onViewPatient={onViewPatient}
              />
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-12 text-center text-ink-soft text-sm">
                No invoices match your filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ==================================================================== */
/* Row                                                                  */
/* ==================================================================== */
function InvoiceRow({
  inv, canDelete, onOpen, onPreview, onCollect, onEmail, onWhatsapp, onDownloadPdf,
  onPrint, onDuplicate, onCancel, onDelete, onViewPatient,
}) {
  const total = Number(inv.total || 0);
  const paid = Number(inv.amount_paid || 0);
  const due = Number(inv.outstanding_amount || 0);
  const status = inv.status || "pending";
  const canCollect = ["pending", "partial", "overdue"].includes(status);
  const daysOverdue = getOverdueDays(inv);

  const t = inv.treatment_summary || {};
  const docs = inv.doctor_names || [];
  const methods = inv.payment_methods || [];

  return (
    <tr className="hover:bg-linen/40 transition-colors" data-testid={`inv-row-${inv.id}`}>
      {/* Invoice */}
      <td className="px-5 py-4 align-top">
        <button
          onClick={() => onOpen(inv)} data-testid={`inv-open-${inv.id}`}
          className="text-left group"
        >
          <div className="font-mono text-[11px] text-ink group-hover:text-pine transition-colors">
            {inv.invoice_number || "—"}
          </div>
          <div className="text-[10px] text-ink-soft mt-1">{fmtDate(inv.created_at)}</div>
        </button>
      </td>

      {/* Patient */}
      <td className="px-3 py-4 align-top">
        <button
          onClick={() => onViewPatient?.(inv)}
          className="text-left group truncate max-w-[180px]"
          data-testid={`inv-patient-${inv.id}`}
        >
          <div className="text-ink font-medium truncate group-hover:text-pine transition-colors">
            {inv.patient_name}
          </div>
          <div className="text-[10px] text-ink-soft mt-0.5 font-mono truncate" data-testid={`inv-patient-meta-${inv.id}`}>
            {inv.clinic_patient_id ? inv.clinic_patient_id : (inv.patient_phone || "—")}
            {inv.clinic_patient_id && inv.patient_phone && (
              <span className="ml-1 opacity-70">· {inv.patient_phone}</span>
            )}
          </div>
        </button>
      </td>

      {/* Treatment */}
      <td className="px-3 py-4 align-top">
        {t.all?.length > 1 ? (
          <Popover>
            <PopoverTrigger asChild>
              <button className="text-left group max-w-[200px]" data-testid={`inv-treatment-${inv.id}`}>
                <div className="text-ink truncate">{t.primary}</div>
                <div className="text-[10px] text-ink-soft mt-0.5 inline-flex items-center gap-1">
                  {t.tooth && <span>Tooth {t.tooth} · </span>}
                  <span className="text-pine group-hover:underline">+{t.more} more</span>
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-2" data-testid={`inv-treatments-popover-${inv.id}`}>
              <div className="overline text-ink-soft px-2 pb-1.5">All treatments</div>
              {t.all.map((x, i) => (
                <div key={i} className="px-2 py-1.5 text-xs text-ink border-b border-[#F5F1E5] last:border-0">
                  {x.procedure}{x.tooth ? ` · Tooth ${x.tooth}` : ""}
                </div>
              ))}
            </PopoverContent>
          </Popover>
        ) : (
          <div className="max-w-[200px]">
            <div className="text-ink truncate">{t.primary || "—"}</div>
            {t.tooth && <div className="text-[10px] text-ink-soft mt-0.5">Tooth {t.tooth}</div>}
          </div>
        )}
      </td>

      {/* Doctor */}
      <td className="px-3 py-4 align-top">
        {docs.length > 1 ? (
          <Popover>
            <PopoverTrigger asChild>
              <button className="text-left group" data-testid={`inv-doctor-${inv.id}`}>
                <div className="text-ink text-sm truncate max-w-[160px]">{docs[0]}</div>
                <div className="text-[10px] text-pine mt-0.5 group-hover:underline">+{docs.length - 1}</div>
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-60 p-2">
              <div className="overline text-ink-soft px-2 pb-1.5">Attending</div>
              {docs.map((d, i) => (
                <div key={i} className="px-2 py-1 text-xs text-ink">{d}</div>
              ))}
            </PopoverContent>
          </Popover>
        ) : (
          <div className="text-ink text-sm truncate max-w-[160px]">
            {docs[0] || <span className="text-ink-soft">Unassigned</span>}
          </div>
        )}
      </td>

      {/* Amount */}
      <td className="px-3 py-4 align-top text-right">
        <div className="font-display text-lg text-ink tabular-nums">{INR(total)}</div>
        {paid > 0 && (
          <div className="text-[10px] text-ink-mid mt-0.5 tabular-nums">
            Paid {INR(paid)}
          </div>
        )}
        {due > 0.01 && (
          <div className="text-[10px] text-clay font-medium mt-0.5 tabular-nums" data-testid={`inv-due-${inv.id}`}>
            Due {INR(due)}
          </div>
        )}
        {methods.length > 0 && (
          <div className="mt-1.5 flex items-center gap-1 justify-end flex-wrap">
            {methods.slice(0, 3).map((m) => (
              <span
                key={m}
                data-testid={`inv-method-chip-${inv.id}-${m}`}
                className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-linen text-ink-mid border border-[#EFEADC]"
              >
                {METHOD_LABEL[m] || m}
              </span>
            ))}
          </div>
        )}
      </td>

      {/* Status */}
      <td className="px-3 py-4 align-top">
        <div className="flex flex-col gap-1 items-start">
          <StatusPill status={status} testid={`inv-status-${inv.id}`} />
          {status === "partial" && (
            <div className="text-[10px] text-ink-soft mt-0.5 font-mono">
              Paid {INR(paid)} · Due {INR(due)}
            </div>
          )}
          {status === "overdue" && daysOverdue > 0 && (
            <div className="text-[10px] text-[#B94A48] font-medium">{daysOverdue} days</div>
          )}
        </div>
      </td>

      {/* Actions */}
      <td className="px-3 py-4 align-top">
        <div className="flex items-center gap-1 justify-end">
          <button
            onClick={() => onOpen(inv)}
            data-testid={`inv-action-view-${inv.id}`}
            title="View invoice"
            className="w-8 h-8 rounded-full grid place-items-center text-ink-mid hover:bg-linen border border-[#EFEADC]"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onPreview(inv)}
            data-testid={`inv-action-preview-${inv.id}`}
            title="Preview invoice document"
            className="w-8 h-8 rounded-full grid place-items-center text-ink-mid hover:bg-linen border border-[#EFEADC]"
          >
            <FileText className="w-3.5 h-3.5" />
          </button>
          {canCollect && (
            <button
              onClick={() => onCollect(inv)}
              data-testid={`inv-action-collect-${inv.id}`}
              title="Collect payment"
              className="text-[11px] px-2.5 py-1.5 rounded-full bg-pine text-linen hover:bg-pine/90 inline-flex items-center gap-1"
            >
              <Banknote className="w-3 h-3" />
              Collect
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`inv-action-more-${inv.id}`}
                title="More"
                className="w-8 h-8 rounded-full grid place-items-center text-ink-mid hover:bg-linen border border-[#EFEADC]"
              >
                <MoreHorizontal className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid={`inv-more-menu-${inv.id}`}>
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-ink-soft">
                Documents
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onDownloadPdf(inv, "digital")}>
                <Download className="w-3.5 h-3.5" /> Download PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onPrint(inv)}>
                <Printer className="w-3.5 h-3.5" /> Print / Letterhead
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-ink-soft">
                Share
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onWhatsapp(inv)}>
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEmail(inv)}>
                <Mail className="w-3.5 h-3.5" /> Email
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onOpen(inv)} data-testid={`inv-more-history-${inv.id}`}>
                <History className="w-3.5 h-3.5" /> Payment history
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onOpen(inv)}>
                <ClipboardList className="w-3.5 h-3.5" /> Audit log
              </DropdownMenuItem>
              {onDuplicate && (
                <DropdownMenuItem onClick={() => onDuplicate(inv)}>
                  <Copy className="w-3.5 h-3.5" /> Duplicate
                </DropdownMenuItem>
              )}
              {status !== "cancelled" && status !== "paid" && (
                <DropdownMenuItem onClick={() => onCancel(inv)} className="text-clay">
                  <XCircle className="w-3.5 h-3.5" /> Cancel invoice
                </DropdownMenuItem>
              )}
              {canDelete && status !== "paid" && (
                <DropdownMenuItem
                  onClick={() => onDelete(inv)}
                  className="text-red-600"
                  data-testid={`inv-more-delete-${inv.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
}


/* -------------------- helper components -------------------- */

function StatusPill({ status, testid }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span
      data-testid={testid}
      className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border font-medium ${m.cls}`}
    >
      {m.label}
    </span>
  );
}

function SortDropdown({ sortId, setSortId }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="inv-sort"
          className="text-xs px-3 py-2 rounded-full border border-[#E8EAE9] bg-white text-ink-mid hover:bg-linen inline-flex items-center gap-1.5"
        >
          <ChevronUp className="w-3 h-3" /><ChevronDown className="w-3 h-3 -ml-2.5" />
          {SORT_KEYS[sortId].label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" data-testid="inv-sort-menu">
        {Object.entries(SORT_KEYS).map(([k, v]) => (
          <DropdownMenuItem key={k} onClick={() => setSortId(k)}
                            data-testid={`inv-sort-${k}`}>
            {v.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FilterPopover({ status, setStatus, method, setMethod, methodOptions }) {
  const [open, setOpen] = useState(false);
  const active = (status !== "all" ? 1 : 0) + (method !== "all" ? 1 : 0);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="inv-filter"
          className={`text-xs px-3 py-2 rounded-full inline-flex items-center gap-1.5 border ${
            active > 0 ? "bg-pine/10 border-pine/30 text-pine" : "bg-white border-[#E8EAE9] text-ink-mid hover:bg-linen"
          }`}
        >
          <Filter className="w-3 h-3" /> Filters {active > 0 && <span className="text-[10px]">({active})</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3 space-y-3" data-testid="inv-filter-menu">
        <div>
          <div className="overline text-ink-soft mb-1.5">Status</div>
          <div className="flex flex-wrap gap-1.5">
            {STATUS_FILTERS.map((s) => (
              <button key={s.id} onClick={() => setStatus(s.id)}
                      data-testid={`inv-filter-status-${s.id}`}
                      className={`text-[11px] px-2.5 py-1 rounded-full border ${
                        status === s.id ? "bg-pine text-linen border-pine" : "bg-white text-ink-mid border-[#E8EAE9]"
                      }`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="overline text-ink-soft mb-1.5">Payment method</div>
          <div className="flex flex-wrap gap-1.5">
            {methodOptions.map((m) => (
              <button key={m} onClick={() => setMethod(m)}
                      data-testid={`inv-filter-method-${m}`}
                      className={`text-[11px] px-2.5 py-1 rounded-full border ${
                        method === m ? "bg-pine text-linen border-pine" : "bg-white text-ink-mid border-[#E8EAE9]"
                      }`}>
                {m === "all" ? "All" : (METHOD_LABEL[m] || m)}
              </button>
            ))}
          </div>
        </div>
        <div className="pt-1 flex justify-end">
          <button onClick={() => { setStatus("all"); setMethod("all"); }}
                  data-testid="inv-filter-clear"
                  className="text-[11px] text-ink-mid hover:text-ink underline">
            Clear all
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


function getOverdueDays(inv) {
  if (inv.status !== "overdue") return 0;
  const anchor = inv.due_date || inv.created_at;
  if (!anchor) return 0;
  const now = new Date();
  const d = new Date(anchor);
  return Math.max(0, Math.floor((now - d) / (1000 * 60 * 60 * 24)));
}
